import { Check } from 'lucide-react'
import React from 'react'

interface ProgressDessertProps {
  completed: number
  total: number
  /** Size of the donut in pixels (minimum 8px enforced) */
  size?: number
  strokeWidth?: number
  displayAsPie?: boolean
}

const EASING_FUNCTION = 'cubic-bezier(0.4, 0, 0.2, 1)'
const DONUT_TRANSITION = 'opacity 0.3s ease-in-out'
const DONUT_TRANSITION_WITH_DELAY = `${DONUT_TRANSITION} 0.6s`

export const ProgressDessert: React.FC<ProgressDessertProps> = ({
  completed,
  total,
  size = 40,
  strokeWidth = 4,
  displayAsPie = false
}) => {
  const _size = Math.max(size, 8) // Minimum 8px
  const _strokeWidth = displayAsPie ? _size / 2 : Math.max(1, Math.min(strokeWidth, _size / 2)) // Minimum 1px, maximum half of the size to prevent stroke overlapping on itself

  // Calculate the percentage completed (0 to 1)
  const percentage = total > 0 ? (completed > 0 ? Math.min(completed / total, 1) : 0.01) : 0
  const isComplete = percentage >= 1

  // SVG circle properties
  const coordinate = _size / 2
  const fillRadius = _size / 2
  const strokeRadius = fillRadius - (_strokeWidth / 2)
  const circumference = 2 * Math.PI * strokeRadius

  // Calculate the dash array for the progress
  // We want to start from the top (12 o'clock position) and go clockwise
  const progressOffset = circumference - (percentage * circumference)

  // Calculate icon size based on donut size
  const iconSize = _size * 0.5

  return (
    <div className='inline-flex items-center justify-center relative'>
      <svg
        width={_size}
        height={_size}
        className='text-thatch-purple-primary transform -rotate-90' // Rotate so progress starts from top
      >
        {/* Background circle (light color, transparent center) */}
        <circle
          cx={coordinate}
          cy={coordinate}
          r={strokeRadius}
          fill='transparent'
          stroke='currentColor'
          strokeWidth={_strokeWidth}
          className={isComplete ? 'opacity-0' : 'opacity-20'} // Hide when complete
          style={{
            transition: isComplete
              ? DONUT_TRANSITION_WITH_DELAY // 0.6s delay when fading out
              : DONUT_TRANSITION // No delay when fading in
          }}
        />

        {/* Progress circle (full color, transparent center, segmented) */}
        <circle
          data-testid='progress-dessert-progress-circle'
          cx={coordinate}
          cy={coordinate}
          r={strokeRadius}
          fill='transparent'
          stroke='currentColor'
          strokeWidth={_strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={progressOffset}
          className={percentage > 0 && !isComplete ? 'opacity-100' : 'opacity-0'} // Hide when complete or no progress
          style={{
            transition: `stroke-dashoffset 0.6s ${EASING_FUNCTION}, ${
              isComplete
                ? DONUT_TRANSITION_WITH_DELAY // 0.6s delay when fading out
                : DONUT_TRANSITION // No delay when fading in
            }`,
          }}
        />

        {/* Full circle (appears at 100% or greater completion) */}
        <circle
          cx={coordinate}
          cy={coordinate}
          r={fillRadius} // Matches the visual area of the stroked donut
          fill='currentColor'
          className='opacity-20'
          style={{
            transition: isComplete
              ? `transform 0.8s ${EASING_FUNCTION} 0.6s` // 0.6s delay when appearing
              : `transform 0.3s ${EASING_FUNCTION} 0.1s`, // Delay when disappearing
            transform: isComplete ? 'scale(1)' : 'scale(0)',
            transformOrigin: 'center'
          }}
        />
      </svg>

      {/* Check icon (appears at 100% or greater completion) */}
      <div
        data-testid='progress-dessert-icon-container'
        className='absolute inset-0 flex items-center justify-center text-thatch-purple-primary'
        style={{
          transition: isComplete
            ? `all 0.4s ${EASING_FUNCTION} 1.0s` // 1.0s delay when appearing (0.6s + 0.4s additional)
            : `all 0.3s ${EASING_FUNCTION}`, // No delay when disappearing
          opacity: isComplete ? 1 : 0,
          transform: isComplete ? 'scale(1)' : 'scale(0.5)',
        }}
      >
        <Check size={iconSize} />
      </div>
    </div>
  )
}

export default ProgressDessert
